import { Component, Structure, ThemeData, ThemeFontMap } from '@wix/thunderbolt-becky-types/dist'
import { MASTER_PAGE_COMPONENTS_IN_RESPONSIVE } from '../../constants'
import { fontObjectToString, parseFontStr } from '../fonts/fontsParser'
import { PinnedChildrenByPositionMap, PinnedPositions } from './pinnedComponentsUtils'
import { ChildrenGroup } from '../../types'

const getIsStructural = ({
	isMobileView,
	isOneDocMigrated,
}: {
	isMobileView?: boolean
	isOneDocMigrated?: boolean
}) => {
	const structuralIDs: { [compId: string]: boolean } = {
		SITE_HEADER: true,
		SITE_FOOTER: true,
		PAGES_CONTAINER: true,
		BACK_TO_TOP_BUTTON: true,
		...(isMobileView
			? {
					SOSP_CONTAINER_CUSTOM_ID: true,
					QUICK_ACTION_BAR: true,
			  }
			: {}),
		...(isOneDocMigrated
			? {}
			: {
					SITE_HEADER_WRAPPER: true,
					'SITE_HEADER-placeholder': true,
					SITE_FOOTER_WRAPPER: true,
					'SITE_FOOTER-placeholder': true,
			  }),
	}

	return (id: string) => Boolean(structuralIDs![id])
}

const getMasterPageChildrenGroupsForMesh = (
	childrenIds: Array<string>,
	structure: Structure,
	isMobileView: boolean,
	isOneDocMigrated: boolean
): { childrenGroups: Array<ChildrenGroup> } => {
	const isStructural = getIsStructural({ isMobileView, isOneDocMigrated })
	const childrenGroups = childrenIds.map((childrenId) => {
		if (isStructural(childrenId)) {
			// TODO: Check if we need to fix the type or the data
			return {
				type: 'STRUCTURAL',
				components: [childrenId],
			} as any
		}
		return {
			type: 'SOAP',
			components: structure[childrenId].components?.map((compId) => structure[compId]),
			id: childrenId,
		} as ChildrenGroup
	})
	return { childrenGroups }
}

const GROUP_TYPES = {
	LEGACY_GAPS: 'LEGACY_GAPS',
	PAGES_CONTAINER: 'PAGES_CONTAINER',
	PINNED: 'PINNED',
	SOAP: 'SOAP',
	STRUCTURAL: 'STRUCTURAL',
} as const

const fixMetaData = (id: string, metaData: Component['metaData'], pageId: string) => {
	if (metaData.pageId === 'masterPage' && !MASTER_PAGE_COMPONENTS_IN_RESPONSIVE.includes(id) && pageId) {
		return {
			...metaData,
			pageId,
		}
	}
	return metaData
}

const resolveThemeFontsSpx = (themeFonts: ThemeData['font']) => {
	const fontMap: ThemeFontMap = {}
	themeFonts.forEach((font, index) => {
		let fontStr = font
		const fontObj = parseFontStr(font)
		if (fontObj.sizeUnit === 'spx') {
			const spxFontObject = {
				...fontObj,
				size: `calc(${fontObj.size} * var(--theme-spx-ratio))`,
				sizeUnit: '',
			}
			fontStr = fontObjectToString(spxFontObject, true)
		}
		fontMap[`font_${index}`] = fontStr
	})

	return fontMap
}

const PINNED_GROUPS_IDS: Record<PinnedPositions, string> = {
	TOP_LEFT: 'pinnedTopLeft',
	TOP_CENTER: 'pinnedTopCenter',
	TOP_RIGHT: 'pinnedTopRight',
	CENTER_LEFT: 'pinnedMiddleLeft',
	CENTER_RIGHT: 'pinnedMiddleRight',
	BOTTOM_LEFT: 'pinnedBottomLeft',
	BOTTOM_CENTER: 'pinnedBottomCenter',
	BOTTOM_RIGHT: 'pinnedBottomRight',
}

export type MasterPageGroup = {
	type: keyof typeof GROUP_TYPES
	components: Array<string>
	id?: string
	parent?: string
}

type ComponentsByType = {
	pinned: PinnedChildrenByPositionMap
	soap: Record<string, Array<string>>
	structural: Array<string>
}

const createPinnedGroup = (components: Array<string>, groupId: string, parent: string): MasterPageGroup => ({
	type: GROUP_TYPES.PINNED,
	components,
	id: groupId,
	parent,
})
const createSOAPGroup = (components: Array<string>, groupId: string): MasterPageGroup => ({
	type: GROUP_TYPES.SOAP,
	components,
	id: groupId,
})
const createStructuralGroup = (compId: string): MasterPageGroup => ({
	type: GROUP_TYPES.STRUCTURAL,
	components: [compId],
})

export {
	GROUP_TYPES,
	fixMetaData,
	resolveThemeFontsSpx,
	ComponentsByType,
	createPinnedGroup,
	createSOAPGroup,
	createStructuralGroup,
	PINNED_GROUPS_IDS,
	getMasterPageChildrenGroupsForMesh,
	getIsStructural,
}
